export interface AccessWindowNoteProps {
  /**
   * How many minutes before the booking starts access opens. Comes from the API payload's
   * `accessWindowMinutes`, never a literal.
   */
  minutes: number
  /** Phrase it as a future event ("becomes available") rather than a current one. */
  future?: boolean
  /**
   * What becomes available. Defaults to nothing, giving "Available 15 minutes before…".
   *
   * The PIN surface needs a subject: its footer used to read "Code becomes available 15
   * minutes before your booking starts", and lifting it here dropped the word, leaving
   * "Becomes available…" — a sentence with no subject, under a panel showing a code.
   */
  subject?: string
}

/**
 * "Available 15 minutes before your booking starts."
 *
 * A component rather than inline strings because the number is a **server** constant
 * (`services/accessControl/constants.ts`). It was previously hardcoded in three places in the
 * mobile page, so changing it server-side would have left the copy quietly lying — in three
 * places, on two surfaces once the web unlock page exists.
 *
 * @example
 * ```tsx
 * <AccessWindowNote minutes={data.accessWindowMinutes} />
 * <AccessWindowNote minutes={data.accessWindowMinutes} future subject="Code" />
 * ```
 */
export function AccessWindowNote({ minutes, future = false, subject }: AccessWindowNoteProps) {
  const verb = future ? 'becomes available' : 'available'

  return (
    <>
      {subject ? `${subject} ${verb} ` : `${verb.charAt(0).toUpperCase()}${verb.slice(1)} `}
      <span className="font-semibold">
        {minutes} {minutes === 1 ? 'minute' : 'minutes'}
      </span>{' '}
      before your booking starts
    </>
  )
}
